import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useProducts } from '@/hooks/useProducts';
import { type Product, getProductPath, getOptimizedImageUrl } from '@/data/allProducts';

export default function PredictiveSearch({ onClose }: { onClose?: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: allProducts = [] } = useProducts();

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!query.trim() || query.length < 2) { setResults([]); setCategories([]); return; }
    const q = query.toLowerCase().trim();
    const matched = allProducts.filter(p =>
      p.title.toLowerCase().includes(q) ||
      p.category.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.tags?.some((t: string) => t.toLowerCase().includes(q))
    ).slice(0, 8);
    setResults(matched);
    const cats = Array.from(new Set(matched.map(p => p.category))).slice(0, 3);
    setCategories(cats);
  }, [query, allProducts]);

  const handleClose = useCallback(() => {
    setQuery('');
    setResults([]);
    onClose?.();
  }, [onClose]);

  return (
    // Fixed on mobile so it never goes off-screen
    <div className="w-full max-w-full overflow-hidden">
      {/* Input */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Escape' && handleClose()}
          placeholder="Search products, categories..."
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className="w-full min-w-0 pl-10 pr-10 py-3 bg-card border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
        />
        {query && (
          <button onClick={() => setQuery('')} aria-label="Clear search" className="absolute end-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Results */}
      {query.length >= 2 && (
        <div className="mt-2 bg-card border border-border rounded-xl overflow-hidden shadow-2xl max-h-[55vh] overflow-y-auto w-full">
          {results.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No products found for <span className="font-semibold text-foreground">"{query}"</span>
            </div>
          ) : (
            <>
              {categories.length > 0 && (
                <div className="px-4 pt-3 pb-2 flex items-center gap-2 flex-wrap border-b border-border/40">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">In:</span>
                  {categories.map(cat => (
                    <Link key={cat} to={`/category/${encodeURIComponent(cat)}`} onClick={handleClose}
                      className="px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                      {cat}
                    </Link>
                  ))}
                </div>
              )}
              <div className="divide-y divide-border/30">
                {results.map(product => (
                  <Link key={product.id} to={getProductPath(product)} onClick={handleClose}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/40 transition-colors group">
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-secondary/50 shrink-0">
                      {product.image
                        ? <img src={getOptimizedImageUrl(product.image, 120)} onError={(e) => { if (e.currentTarget.src !== product.image) e.currentTarget.src = product.image; }} alt={product.title} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs font-bold">Z</div>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{product.title}</p>
                      <p className="text-xs text-muted-foreground">{product.category} · EGP {product.price.toLocaleString()}</p>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                  </Link>
                ))}
              </div>
              <Link to={`/products?search=${encodeURIComponent(query)}`} onClick={handleClose}
                className="flex items-center justify-center gap-2 px-4 py-3 text-xs font-semibold text-primary hover:bg-primary/5 transition-colors border-t border-border/40">
                View all results for "{query}" <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}



